import { useEffect, useMemo, useRef, useState } from "react";
import {
  ControlSessionSummary,
  SessionStatusFilter,
} from "../../../../hooks/useControlsV2";
import { cn } from "../../../../lib/utils";

type StatusKey = "not_started" | SessionStatusFilter;

const STATUS_ORDER: StatusKey[] = [
  "not_started",
  "in_progress",
  "in_review",
  "complete",
];

const barColor: Record<StatusKey, string> = {
  not_started: "bg-stone-300",
  in_progress: "bg-blue-400",
  in_review: "bg-amber-400",
  complete: "bg-green-500",
};

const barHoverColor: Record<StatusKey, string> = {
  not_started: "",
  in_progress: "hover:bg-blue-500",
  in_review: "hover:bg-amber-500",
  complete: "hover:bg-green-600",
};

const boxActiveBg: Record<SessionStatusFilter, string> = {
  in_progress: "bg-blue-50",
  in_review: "bg-amber-50",
  complete: "bg-green-50",
};

const boxActiveHoverBg: Record<SessionStatusFilter, string> = {
  in_progress: "hover:bg-blue-100",
  in_review: "hover:bg-amber-100",
  complete: "hover:bg-green-100",
};

const boxActiveIndicator: Record<SessionStatusFilter, string> = {
  in_progress: "bg-blue-500",
  in_review: "bg-amber-500",
  complete: "bg-green-600",
};

const boxActiveNumberColor: Record<SessionStatusFilter, string> = {
  in_progress: "text-blue-600",
  in_review: "text-amber-600",
  complete: "text-green-600",
};

// Tile labels are the only stable thing distinguishing one stat from the next.
const statKey = (label: string) => label.toLowerCase().replace(/\s+/g, "-");

interface StatBoxProps {
  label: string;
  value: number;
  subtitle: string;
  status: SessionStatusFilter;
  isActive: boolean;
  onClick: () => void;
}

function StatBox({
  label,
  value,
  subtitle,
  status,
  isActive,
  onClick,
}: StatBoxProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex-1 px-4 py-3 text-left transition-colors",
        isActive
          ? cn(boxActiveBg[status], boxActiveHoverBg[status])
          : "bg-white hover:bg-stone-50",
      )}
    >
      {isActive && (
        <div
          className={cn(
            "absolute inset-x-0 top-0 h-0.5",
            boxActiveIndicator[status],
          )}
        />
      )}
      <p className="text-[11px] font-medium text-stone-500 uppercase tracking-wide mb-1.5">
        {label}
      </p>
      <p
        data-testid={`fieldwork-stat-${statKey(label)}-value`}
        className={cn(
          "text-xl font-semibold",
          isActive ? boxActiveNumberColor[status] : "text-stone-900",
        )}
      >
        {value}
      </p>
      <p
        data-testid={`fieldwork-stat-${statKey(label)}-subtitle`}
        className="text-xs text-stone-500 mt-0.5"
      >
        {subtitle}
      </p>
    </button>
  );
}

interface StatsStripProps {
  sessionSummary: ControlSessionSummary | null;
  totalCount: number;
  sessionStatusFilters: SessionStatusFilter[];
  onSessionStatusFiltersChange: (filters: SessionStatusFilter[]) => void;
  /** Noun for `totalCount`. Defaults to "controls" for the flag-off
      FieldworkPage; the unified page passes a both-entity noun. */
  totalCountLabel?: string;
}

export function StatsStrip({
  sessionSummary,
  totalCount,
  sessionStatusFilters,
  onSessionStatusFiltersChange,
  totalCountLabel,
}: StatsStripProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(0);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setBarWidth(el.getBoundingClientRect().width);
    });
    ro.observe(el);
    setBarWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const stats = useMemo(() => {
    if (!sessionSummary) {
      return {
        totalSessions: 0,
        complete: 0,
        inProgress: 0,
        inReview: 0,
        notStarted: 0,
        progressPct: 0,
      };
    }
    const { total, complete, inProgress, inReview } = sessionSummary;
    const notStarted = Math.max(0, total - complete - inProgress - inReview);
    const progressPct = total > 0 ? Math.round((complete / total) * 100) : 0;
    return {
      totalSessions: total,
      complete,
      inProgress,
      inReview,
      notStarted,
      progressPct,
    };
  }, [sessionSummary]);

  const toggleFilter = (status: SessionStatusFilter) => {
    const active = sessionStatusFilters.includes(status);
    onSessionStatusFiltersChange(
      active
        ? sessionStatusFilters.filter((s) => s !== status)
        : [...sessionStatusFilters, status],
    );
  };

  const clearFilters = () => onSessionStatusFiltersChange([]);

  const hasFilters = sessionStatusFilters.length > 0;
  const isActive = (status: SessionStatusFilter) =>
    sessionStatusFilters.includes(status);

  // Bar segments sorted by status order, only those with count > 0
  const barSegments = useMemo(
    () =>
      STATUS_ORDER.map((status) => ({
        status,
        count:
          status === "not_started"
            ? stats.notStarted
            : status === "in_progress"
              ? stats.inProgress
              : status === "in_review"
                ? stats.inReview
                : stats.complete,
      })).filter((s) => s.count > 0),
    [stats],
  );

  // Tick width for the visual grid overlay
  const tickWidth =
    stats.totalSessions > 0 && barWidth > 0
      ? Math.max(3, barWidth / stats.totalSessions)
      : 12;
  const separatorW = Math.max(0.5, tickWidth * 0.1);
  const colorW = tickWidth - separatorW;
  const tickGradient = `repeating-linear-gradient(90deg, transparent 0, transparent ${colorW}px, rgba(255,255,255,0.4) ${colorW}px, rgba(255,255,255,0.4) ${tickWidth}px)`;

  return (
    <div className="shrink-0 px-6 pt-6 pb-4 border-b border-stone-200">
      {/* Stat boxes */}
      <div className="flex items-stretch gap-[1px] bg-stone-200 border border-stone-200">
        {/* Tests Progress — clears all status filters */}
        <button
          onClick={clearFilters}
          className="relative flex-1 bg-white px-4 py-3 text-left transition-colors hover:bg-stone-50"
        >
          <p className="text-[11px] font-medium text-stone-500 uppercase tracking-wide mb-1.5">
            Tests Progress
          </p>
          <p
            data-testid="fieldwork-stat-tests-progress-value"
            className="text-xl font-semibold text-stone-900"
          >
            {stats.complete} / {stats.totalSessions} complete
          </p>
          <p
            data-testid="fieldwork-stat-tests-progress-subtitle"
            className="text-xs text-stone-500 mt-0.5"
          >
            {stats.progressPct}% complete · {totalCount}{" "}
            {totalCountLabel ?? "controls"}
          </p>
        </button>

        <StatBox
          label="In Progress"
          value={stats.inProgress}
          subtitle={stats.inProgress === 1 ? "active test" : "active tests"}
          status="in_progress"
          isActive={isActive("in_progress")}
          onClick={() => toggleFilter("in_progress")}
        />

        <StatBox
          label="Pending Review"
          value={stats.inReview}
          subtitle={
            stats.inReview === 1 ? "awaiting review" : "awaiting review"
          }
          status="in_review"
          isActive={isActive("in_review")}
          onClick={() => toggleFilter("in_review")}
        />

        <StatBox
          label="Complete"
          value={stats.complete}
          subtitle={`of ${stats.totalSessions} tests`}
          status="complete"
          isActive={isActive("complete")}
          onClick={() => toggleFilter("complete")}
        />
      </div>

      {/* Segmented bar */}
      {stats.totalSessions > 0 && (
        <div className="mt-3">
          <p className="text-xs text-stone-500 mb-1.5">
            {stats.complete} / {stats.totalSessions} tests complete
            {stats.inReview > 0 && ` · ${stats.inReview} pending review`}
            {stats.inProgress > 0 && ` · ${stats.inProgress} in progress`}
            {stats.notStarted > 0 && ` · ${stats.notStarted} not started`}
          </p>
          <div
            ref={barRef}
            className="relative flex h-8 overflow-hidden border border-stone-200"
          >
            {/* Tick mark overlay — shared across all segments for alignment */}
            <div
              className="absolute inset-0 z-10 pointer-events-none"
              style={{ backgroundImage: tickGradient }}
            />

            {barSegments.map(({ status, count }) => {
              const filterable = status !== "not_started";
              const active =
                filterable && isActive(status as SessionStatusFilter);
              const dimmed = hasFilters && !active;
              return (
                <button
                  key={status}
                  style={{ flex: count }}
                  disabled={!filterable}
                  onClick={() =>
                    filterable && toggleFilter(status as SessionStatusFilter)
                  }
                  title={`${status.replace(/_/g, " ")}: ${count}`}
                  className={cn(
                    "h-full transition-all",
                    barColor[status],
                    filterable && barHoverColor[status],
                    filterable ? "cursor-pointer" : "cursor-default",
                    dimmed && "opacity-25 hover:opacity-60",
                  )}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
